import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChartDataPoint, TimeOfDayConfig } from './types';
import { TIME_COLOR_SCHEMES, formatTimeMetricValue, getTimeMetricDisplayName } from './utils';

interface TimeOfDayChartProps {
  data: ChartDataPoint[];
  config: TimeOfDayConfig;
  // peakAnalysis: PeakHourAnalysis;
  onHourClick?: (hour: number, data: ChartDataPoint) => void;
}

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, config }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const value = payload[0].value;

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[150px]">
      <div className="font-medium text-gray-900 mb-2">
        {data.formattedHour}
        {!config.show24Hour && (
          <span className="text-sm text-gray-500 ml-1">
            {data.period}
          </span>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {getTimeMetricDisplayName(config.selectedMetric)}
            :
          </span>
          <span className="font-medium text-gray-900 ml-1">
            {formatTimeMetricValue(value, config.selectedMetric)}
          </span>
        </div>
        {/* {data.isPeakHour && (
          <div className="text-xs text-orange-600 font-medium">
            🔥 Peak Hour
          </div>
        )} */}
      </div>
    </div>
  );
};

const TimeOfDayChart: React.FC<TimeOfDayChartProps> = ({
  data,
  config,
  // peakAnalysis,
  onHourClick,
}) => {
  const colorScheme = TIME_COLOR_SCHEMES[config.colorScheme];

  // Handle click events
  const handleClick = (data: any) => {
    if (onHourClick && data?.activePayload?.[0]?.payload) {
      const clickedData = data.activePayload[0].payload;
      onHourClick(clickedData.hour, clickedData);
    }
  };

  // Common chart props
  const commonProps = {
    data,
    margin: { top: 20, right: 30, left: 20, bottom: 20 },
    onClick: handleClick,
  };

  // Common axis props
  const xAxisProps = {
    dataKey: 'formattedHour',
    tick: { fontSize: 12, fill: '#6B7280' },
    tickLine: { stroke: '#E5E7EB' },
    axisLine: { stroke: '#E5E7EB' },
  };

  const yAxisProps = {
    tick: { fontSize: 12, fill: '#6B7280' },
    tickLine: { stroke: '#E5E7EB' },
    axisLine: { stroke: '#E5E7EB' },
    tickFormatter: (value: number) => formatTimeMetricValue(value, config.selectedMetric),
  };

  const renderChart = () => {
    switch (config.chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {config.showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {config.showTooltips && (
              <Tooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    config={config}
                  />
                )}
                cursor={{ fill: colorScheme.background, opacity: 0.5 }}
              />
            )}
            {/* {config.showAverageLines && (
              <ReferenceLine
                y={0}
                stroke={colorScheme.secondary}
                strokeDasharray="5 5"
                label={{ value: 'Average', position: 'top', fontSize: 12 }}
              />
            )} */}
            <Bar
              dataKey="value"
              fill={colorScheme.primary}
              radius={[4, 4, 0, 0]}
              cursor="pointer"
            />
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            {config.showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {config.showTooltips && (
              <Tooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    config={config}
                  />
                )}
              />
            )}
            {/* {config.showAverageLines && (
              <ReferenceLine
                y={0}
                stroke={colorScheme.secondary}
                strokeDasharray="5 5"
                label={{ value: 'Average', position: 'top', fontSize: 12 }}
              />
            )} */}
            <defs>
              <linearGradient
                id="areaGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={colorScheme.primary}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={colorScheme.primary}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={colorScheme.primary}
              strokeWidth={2}
              fill="url(#areaGradient)"
              cursor="pointer"
            />
          </AreaChart>
        );

      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            {config.showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            {config.showTooltips && (
              <Tooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    config={config}
                  />
                )}
              />
            )}
            {/* {config.showAverageLines && (
              <ReferenceLine
                y={peakAnalysis.averageValue}
                stroke={colorScheme.secondary}
                strokeDasharray="5 5"
                label={{ value: 'Average', position: 'top', fontSize: 12 }}
              />
            )} */}
            <Line
              type="monotone"
              dataKey="value"
              stroke={colorScheme.primary}
              strokeWidth={3}
              dot={{
                fill: colorScheme.primary,
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                r: 6,
                fill: colorScheme.primary,
                stroke: '#ffffff',
                strokeWidth: 2,
              }}
              cursor="pointer"
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="w-full">
      <ResponsiveContainer
        width="100%"
        height={config.chartHeight}
      >
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default TimeOfDayChart;
